import React from 'react';
import LegalPageLayout from './LegalPageLayout';

export default function TermsOfService() {
  return (
    <LegalPageLayout title="Terms of Service" testId="rwm-terms-of-service">
      <h2>Introduction</h2>
      <p>
        These Terms of Service govern your use of RemoteWorkMarket.com, a global remote-only job
        board operated by ONG IT Solutions in the Netherlands. By creating an account or using
        the platform, you agree to these terms and our Privacy Policy.
      </p>

      <h2>Eligibility</h2>
      <p>
        You must be at least 18 years old to use the platform. Companies must represent a
        legitimate business entity. Candidates must provide accurate personal information.
        We reserve the right to refuse or terminate access for users who do not meet these
        requirements.
      </p>

      <h2>Company Obligations</h2>
      <ul>
        <li>Post only genuine remote job listings with disclosed salary information.</li>
        <li>Respond to applications within 30 days and assign a final status to every application.</li>
        <li>Do not post fake listings, MLM schemes, or discriminatory content.</li>
        <li>Maintain accurate company profile information, including VAT details where applicable.</li>
        <li>Comply with applicable employment and anti-discrimination laws in your jurisdiction.</li>
      </ul>

      <h2>Candidate Obligations</h2>
      <ul>
        <li>The platform is free to use for job seekers.</li>
        <li>Provide accurate information in your profile and applications.</li>
        <li>Submit only genuine CVs and cover letters that belong to you.</li>
        <li>Apply in good faith for roles you genuinely intend to pursue.</li>
        <li>
          Account deletion triggers GDPR anonymization of your application data as described in
          our Privacy Policy.
        </li>
      </ul>

      <h2>Payments and Packages</h2>
      <ul>
        <li>Listing credits must be purchased before a job can be submitted for review.</li>
        <li>Payments are processed securely via Stripe; we do not store card data.</li>
        <li>Credits are consumed on a first-in, first-out basis when a job is published.</li>
        <li>Refunds are not provided except where the platform is at fault.</li>
        <li>All prices are displayed exclusive of VAT where applicable.</li>
      </ul>

      <h2>Acceptable Use</h2>
      <ul>
        <li>All job listings are manually reviewed before going live.</li>
        <li>We reserve the right to reject or remove any listing at our discretion.</li>
        <li>
          You may not scrape, reverse-engineer, or attempt to circumvent platform security
          measures.
        </li>
        <li>
          RemoteWorkMarket.com is a job board platform — we are not a recruitment agency and do
          not participate in hiring decisions.
        </li>
      </ul>

      <h2>Termination</h2>
      <p>
        We may suspend or permanently ban accounts that violate these terms, post prohibited
        content, or engage in fraudulent activity. You may delete your account at any time.
        Upon termination, your right to use the platform ceases immediately, subject to data
        retention obligations described in our Privacy Policy.
      </p>

      <h2>Anti-Ghosting Obligations</h2>
      <p>
        Companies using RemoteWorkMarket commit to responding to all applications within 30 days
        of receipt. Before archiving a job listing, companies must assign a final status (Hired
        or Not Selected) to every application received through the platform. Failure to meet
        these obligations may result in suspension of the company account. ONG IT Solutions
        reserves the right to enforce these obligations at its discretion.
      </p>

      <h2>Listing Rejection and Removal</h2>
      <p>
        ONG IT Solutions reserves the right to reject or remove any job listing at any time,
        for any reason, including listings that were previously approved. Reasons may include,
        but are not limited to, violations of these Terms, complaints received, or changes in
        platform policy. No refund is issued for listings that are removed after approval.
      </p>

      <h2>Reactivation Fee Policy</h2>
      <p>
        If a company&apos;s job listing has been rejected three or more times, ONG IT Solutions
        may require the company to pay a reactivation fee before submitting new listings. The
        reactivation fee amount is set by ONG IT Solutions and may change at any time. Companies
        subject to the reactivation fee will be notified within the platform. The reactivation
        fee is non-refundable.
      </p>

      <h2>Changes to Pricing</h2>
      <p>
        ONG IT Solutions reserves the right to change the pricing of any service or feature on
        RemoteWorkMarket at any time, without prior notice. Price changes take effect immediately
        upon publication on the platform. Users who have already purchased a package at a previous
        price are not entitled to a refund or credit based on any subsequent price change.
      </p>

      <h2>Skills and Platform Content</h2>
      <p>
        The list of skills available on RemoteWorkMarket is maintained by ONG IT Solutions. We
        reserve the right to add, modify, rename, or remove skills at any time. Such changes may
        affect the skills displayed on existing job listings. ONG IT Solutions is not liable for
        any impact these changes may have on listing visibility or search results.
      </p>

      <h2>Platform Changes and Availability</h2>
      <p>
        ONG IT Solutions reserves the right to modify, suspend, or discontinue RemoteWorkMarket
        or any part of it at any time, with or without notice. This includes adding, changing,
        or removing features, integrations, or content. ONG IT Solutions is not liable to you or
        any third party for any such modification, suspension, or discontinuation of the platform
        or any of its features.
      </p>

      <h2>Updates to These Terms</h2>
      <p>
        ONG IT Solutions may update these Terms of Service at any time. When we make material
        changes, we will notify registered users by email. Continued use of the platform after
        changes are published constitutes acceptance of the updated Terms. We recommend reviewing
        these Terms periodically. The date of the most recent update is indicated at the top of
        this page.
      </p>

      <h2>Limitation of Liability</h2>
      <ul>
        <li>
          We are not responsible for hiring decisions made by companies or outcomes of the
          application process.
        </li>
        <li>
          We enforce anti-ghosting requirements on a best-effort basis but cannot guarantee
          company behaviour beyond platform rules and account enforcement.
        </li>
        <li>
          The platform is provided &quot;as is&quot; to the fullest extent permitted by applicable law.
        </li>
      </ul>

      <h2>Governing Law</h2>
      <p>
        These terms are governed by the laws of the Kingdom of the Netherlands. Any disputes
        shall be subject to the exclusive jurisdiction of the courts of Amsterdam.
      </p>

      <h2>Contact</h2>
      <p>
        For questions about these terms, contact{' '}
        <a href="mailto:support@remoteworkmarket.com">support@remoteworkmarket.com</a>.
      </p>
      <p className="mb-0">ONG IT Solutions, Netherlands</p>
    </LegalPageLayout>
  );
}
